import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import AppHeader from '../../components/AppHeader';
import { useTheme } from '../../contexts/ThemeContext';
import { useSettings } from '../../contexts/SettingsContext';
import { getMetadata } from '../../utils/encryptedStorage';
import aiAutomationApi from '../../utils/aiAutomationApi';
import NotificationFilters from '../../components/notifications/NotificationFilters';
import NotificationIcon from '../../components/notifications/NotificationIcon';
import NotificationItem from '../../components/notifications/NotificationItem';
import {
  filterNotificationsByCategory,
  filterNotificationsByType,
  formatNotificationDate,
  getNotificationCountsByCategory,
  getNotificationNavigationTarget,
  getNotificationTypeLabel,
  normalizeNotifications,
} from '../../utils/notificationUtils';

const NotificationsPage = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { isDark } = useTheme();
  const { settings } = useSettings();
  const primaryColor = settings?.primaryColor || '#3B5BDB';

  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(new Set());
  const [deleting, setDeleting] = useState(false);
  const [markingRead, setMarkingRead] = useState(false);
  const [activeCategory, setActiveCategory] = useState(() => searchParams.get('category') || 'all');
  const [actionMessage, setActionMessage] = useState('');

  const fetchNotifications = useCallback(async () => {
    setLoading(true);
    try {
      const meta = await getMetadata();
      const cid = meta?.cid;
      const params = cid ? `?cid=${cid}` : '';
      const data = await aiAutomationApi.get(`/interviews/action-notifications${params}`);
      if (data?.notifications) {
        const normalized = normalizeNotifications(data.notifications).map((n, idx) => ({
          ...n,
          notification_id: n.notification_id || n.id || idx,
          view_status: n.view_status ?? false,
        }));
        setNotifications(normalized);
      }
    } catch (err) {
      console.error('Failed to fetch notifications:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchNotifications();
  }, [fetchNotifications]);

  useEffect(() => {
    setActiveCategory(searchParams.get('category') || 'all');
    setSelected(new Set());
  }, [searchParams]);

  const notificationCounts = getNotificationCountsByCategory(notifications);
  const activeType = searchParams.get('type') || '';
  const activeTypeLabel = activeType ? getNotificationTypeLabel(activeType) : '';
  const filteredByCategory = filterNotificationsByCategory(notifications, activeCategory);
  const filteredNotifications = filterNotificationsByType(filteredByCategory, activeType);
  const filteredNotificationIds = new Set(filteredNotifications.map((n) => n.notification_id));
  const selectedVisibleCount = Array.from(selected).filter((id) => filteredNotificationIds.has(id)).length;
  const unread = filteredNotifications.filter((n) => !n.view_status);
  const read = filteredNotifications.filter((n) => n.view_status);

  const toggleSelect = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (filteredNotifications.length > 0 && selectedVisibleCount === filteredNotifications.length) {
      setSelected((prev) => new Set(Array.from(prev).filter((id) => !filteredNotificationIds.has(id))));
    } else {
      setSelected((prev) => {
        const next = new Set(prev);
        filteredNotifications.forEach((n) => next.add(n.notification_id));
        return next;
      });
    }
  };

  const handleCategoryChange = (category) => {
    const nextParams = new URLSearchParams(searchParams);
    if (!category || category === 'all') nextParams.delete('category');
    else nextParams.set('category', category);
    nextParams.delete('type');
    setActiveCategory(category || 'all');
    setSelected(new Set());
    setSearchParams(nextParams, { replace: true });
  };

  const clearTypeFilter = () => {
    const nextParams = new URLSearchParams(searchParams);
    nextParams.delete('type');
    setSelected(new Set());
    setSearchParams(nextParams, { replace: true });
  };

  const handleDelete = async () => {
    if (selected.size === 0) return;
    setDeleting(true);
    try {
      await aiAutomationApi.delete('/interviews/notifications', {
        body: { notification_ids: Array.from(selected) },
      });
      setNotifications((prev) => prev.filter((n) => !selected.has(n.notification_id)));
      setSelected(new Set());
    } catch (err) {
      console.error('Failed to delete notifications:', err);
    } finally {
      setDeleting(false);
    }
  };

  const handleMarkAllRead = async () => {
    setMarkingRead(true);
    try {
      const meta = await getMetadata();
      const cid = meta?.cid;
      const params = cid ? `?cid=${cid}` : '';
      await aiAutomationApi.patch(`/interviews/notifications/clear-all${params}`);
      setNotifications((prev) => prev.map((n) => ({ ...n, view_status: true })));
    } catch (err) {
      console.error('Failed to mark all read:', err);
    } finally {
      setMarkingRead(false);
    }
  };

  const handleNotificationOpen = async (notif) => {
    setActionMessage('');
    const target = getNotificationNavigationTarget(notif);
    if (!target?.path) {
      setActionMessage('This notification does not have enough details to open the related record.');
      return;
    }

    navigate(target.path);
    setNotifications((prev) =>
      prev.map((n) =>
        n.notification_id === notif.notification_id
          ? { ...n, view_status: true, isUnread: false }
          : n
      )
    );

    try {
      await aiAutomationApi.patch(`/interviews/notifications/${notif.id}/read?notification_type=${notif.type}`);
      window.dispatchEvent(new CustomEvent('notificationsChanged'));
    } catch (err) {
      console.error('Failed to mark notification as read:', err);
    }
  };

  const renderRow = (notif) => {
    return (
      <NotificationItem
        key={notif.notification_id}
        notification={notif}
        selectable
        selected={selected.has(notif.notification_id)}
        onSelect={toggleSelect}
        onOpen={handleNotificationOpen}
        showUnreadDot
        timeLabel={formatNotificationDate(notif.created_at)}
      />
    );
  };

  return (
    <div className={`min-h-screen transition-colors duration-250 ${isDark ? 'bg-gray-900 text-white' : 'bg-[#F8F9FA] text-gray-900'}`}>
      <AppHeader />
      <div className="w-full p-3 md:p-6">
        {/* Page Header */}
        <div className="mb-5 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex min-w-0 items-center gap-3">
            <button
              onClick={() => navigate(-1)}
              className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-lg bg-white text-gray-500 shadow-sm ring-1 ring-gray-200 transition-colors hover:bg-gray-100 hover:text-gray-700 dark:bg-gray-800 dark:text-gray-400 dark:ring-gray-700 dark:hover:bg-gray-700 dark:hover:text-gray-200"
              aria-label="Go back"
            >
              <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M15 19l-7-7 7-7" />
              </svg>
            </button>
            <div className="min-w-0">
              <div className="flex items-center gap-2">
                <span className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-xl bg-[#EEF2FF] text-[#3B5BDB]">
                  <NotificationIcon icon="bell" />
                </span>
                <div className="min-w-0">
                  <h1 className="truncate text-xl font-semibold text-gray-900 dark:text-white">Notifications</h1>
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    {notifications.length > 0
                      ? `${notifications.length} total notification${notifications.length === 1 ? '' : 's'}`
                      : 'Review alerts and recent activity'}
                  </p>
                </div>
              </div>
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-2 sm:justify-end">
            {unread.length > 0 && (
              <button
                onClick={handleMarkAllRead}
                disabled={markingRead}
                className="rounded-full border border-gray-200 bg-white px-4 py-2 text-sm font-medium text-gray-600 shadow-sm transition-colors hover:bg-gray-50 disabled:opacity-50 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-300 dark:hover:bg-gray-700"
              >
                {markingRead ? 'Marking...' : 'Mark All Read'}
              </button>
            )}
            {selected.size > 0 && (
              <button
                onClick={handleDelete}
                disabled={deleting}
                className="rounded-full bg-red-500 px-4 py-2 text-sm font-medium text-white shadow-sm transition-colors hover:bg-red-600 disabled:opacity-50"
              >
                {deleting ? 'Deleting...' : `Delete Selected (${selected.size})`}
              </button>
            )}
          </div>
        </div>

        {actionMessage && (
          <div className="mb-4 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800 dark:border-amber-900/50 dark:bg-amber-900/20 dark:text-amber-200">
            {actionMessage}
          </div>
        )}

        {/* Card */}
        <div className="overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm dark:border-gray-700 dark:bg-gray-800">
          {/* Toolbar */}
          {notifications.length > 0 && (
            <div className="flex flex-col gap-3 border-b border-gray-100 bg-[#F9FAFB] px-4 py-3 dark:border-gray-700 dark:bg-gray-700/40 sm:flex-row sm:items-center sm:justify-between">
              <div className="flex items-center gap-3">
                <input
                  type="checkbox"
                  checked={filteredNotifications.length > 0 && selectedVisibleCount === filteredNotifications.length}
                  onChange={toggleSelectAll}
                  className="cursor-pointer accent-blue-500"
                />
                <span className="text-xs text-gray-500 dark:text-gray-400">
                  {selectedVisibleCount > 0 ? `${selectedVisibleCount} selected` : 'Select visible'}
                </span>
              </div>
              <NotificationFilters
                activeCategory={activeCategory}
                counts={notificationCounts}
                onChange={handleCategoryChange}
              />
            </div>
          )}

          {activeType && (
            <div className="flex items-center justify-between gap-3 border-b border-gray-100 bg-white px-4 py-2.5 dark:border-gray-700 dark:bg-gray-800">
              <p className="text-xs text-gray-500 dark:text-gray-400">
                Showing <span className="font-semibold text-gray-700 dark:text-gray-200">{activeTypeLabel}</span> notifications
              </p>
              <button
                type="button"
                onClick={clearTypeFilter}
                className="rounded-full bg-gray-100 px-3 py-1 text-xs font-medium text-gray-600 transition-colors hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600"
              >
                Clear filter
              </button>
            </div>
          )}

          {loading ? (
            <div className="flex items-center justify-center py-16">
              <div
                className="animate-spin rounded-full h-8 w-8 border-2 border-gray-200 dark:border-gray-600"
                style={{ borderTopColor: primaryColor }}
              />
            </div>
          ) : notifications.length === 0 ? (
            <div className="text-center py-16 px-4">
              <NotificationIcon className="mx-auto h-8 w-8 text-gray-400" />
              <p className="mt-4 text-gray-500 dark:text-gray-400">No notifications yet</p>
              <p className="text-sm text-gray-400 dark:text-gray-500 mt-1">You're all caught up!</p>
            </div>
          ) : filteredNotifications.length === 0 ? (
            <div className="px-4 py-16 text-center">
              <NotificationIcon className="mx-auto h-8 w-8 text-gray-400" />
              <p className="mt-4 text-gray-500 dark:text-gray-400">
                {activeType ? `No ${activeTypeLabel} notifications` : 'No notifications in this category'}
              </p>
              <p className="mt-1 text-sm text-gray-400 dark:text-gray-500">
                {activeType ? 'Clear the type filter to see other notifications.' : 'Try another notification filter.'}
              </p>
            </div>
          ) : (
            <>
              {/* Unread section */}
              {unread.length > 0 && (
                <>
                  <div className="px-4 py-2 bg-gray-50 dark:bg-gray-700/40 border-b border-gray-100 dark:border-gray-700">
                    <span className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Unread ({unread.length})
                    </span>
                  </div>
                  {unread.map(renderRow)}
                </>
              )}

              {/* Read section */}
              {read.length > 0 && (
                <>
                  <div className="px-4 py-2 bg-gray-50 dark:bg-gray-700/40 border-b border-gray-100 dark:border-gray-700">
                    <span className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Read ({read.length})
                    </span>
                  </div>
                  {read.map(renderRow)}
                </>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default NotificationsPage;
